import React, { useState, useRef, useEffect } from 'react';
import { Clock, AlertCircle, RefreshCw } from 'lucide-react';
import api from '../services/api';

/**
 * Provider-aware recording player.
 *
 * Renders a native <audio> element with the proxied URL. The element
 * loads lazily (preload="none") so the actual recording fetch only
 * happens when the user clicks Play — no background HEAD probes from
 * the inbox thread (which would otherwise fan out into N parallel
 * VoIP.ms downloads when a conversation has many recorded calls).
 *
 * When the underlying GET returns 202 (VoIP.ms hasn't finalised the
 * .mp3 yet) or 404 (recording never existed), the <audio> element
 * fires an error event. We intercept it, ask the route what really
 * happened, and render either:
 *  - amber "still finalising" banner with manual retry, OR
 *  - gray "not available" hint.
 */
const STATUS = { READY: 'ready', PENDING: 'pending', UNAVAILABLE: 'unavailable', ERROR: 'error' };

const VoipRecordingPlayer = ({ kind, id, className = '', srcPath }) => {
  const [status, setStatus] = useState(STATUS.READY);
  const [version, setVersion] = useState(0);
  const audioRef = useRef(null);

  const tokenParam = encodeURIComponent(localStorage.getItem('token') || '');
  // srcPath overrides the default VoIP proxy path — used by Phone Agent
  // recordings, which stream through /phone-agent/recording/… instead.
  const recordingUrl = `${api.defaults.baseURL}/${srcPath || `voip/recording/${kind}/${id}`}?token=${tokenParam}&v=${version}`;

  // When the <audio> element fails, check what the route actually
  // returned — same URL, but a GET we can read the status off. This
  // only fires when the user clicked Play, so at most one round-trip
  // per actual interaction.
  const onAudioError = async () => {
    try {
      const res = await fetch(recordingUrl, { method: 'GET' });
      if (res.status === 202) setStatus(STATUS.PENDING);
      else if (res.status === 404) setStatus(STATUS.UNAVAILABLE);
      else setStatus(STATUS.ERROR);
    } catch (_) {
      setStatus(STATUS.ERROR);
    }
  };

  const retry = () => {
    setStatus(STATUS.READY);
    // Bump the version query param so the <audio> element treats it as
    // a different src and re-fetches instead of using its cached failure.
    setVersion((v) => v + 1);
  };

  // Reset when the underlying recording changes (different note / message).
  useEffect(() => {
    setStatus(STATUS.READY);
    setVersion(0);
  }, [kind, id]);

  if (status === STATUS.PENDING) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 ${className}`}>
        <Clock size={14} className="text-amber-700 flex-shrink-0" />
        <span className="flex-1">
          Recording is being finalised by Gain Rhino VoIP (usually 5–10 min after the call).
        </span>
        <button
          type="button"
          onClick={retry}
          className="px-2 py-0.5 text-amber-800 underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (status === STATUS.UNAVAILABLE) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-xs text-gray-600 ${className}`}>
        <AlertCircle size={14} className="text-gray-500 flex-shrink-0" />
        No recording available for this call.
      </div>
    );
  }

  if (status === STATUS.ERROR) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-800 ${className}`}>
        <AlertCircle size={14} className="text-red-700 flex-shrink-0" />
        <span className="flex-1">Could not load recording.</span>
        <button
          type="button"
          onClick={retry}
          className="px-2 py-0.5 text-red-800 underline hover:no-underline"
        >
          <RefreshCw size={12} className="inline mr-1" />Retry
        </button>
      </div>
    );
  }

  return (
    <audio
      ref={audioRef}
      controls
      preload="none"
      src={recordingUrl}
      onError={onAudioError}
      className={`w-full max-w-md ${className}`}
    />
  );
};

export default VoipRecordingPlayer;
